import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { map, Observable, startWith } from 'rxjs';

@Component({
	selector: 'app-worktask-dialog',
	templateUrl: 'worktask-dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class WorktaskDialogComponent implements OnInit {
	@Output() saved: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	httpError: string = '';
	loading: boolean = false;
	worktask: IWorktask;
	offstampId = '00000000-0000-0000-0000-000000000000';
	isEdit = false;
	editWorktask: IWorktask;
	categoryFilteredOptions: Observable<string[]>;
	categoryControl: FormControl<string>;
	uncategorizedName: string;
	terminals: ITerminal[] = [];

	constructor(
		public dialogRef: MatDialogRef<WorktaskDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		public dialogData: { worktask: IWorktask; categoryNames: string[] },
		public worktaskService: WorktaskService,
		public terminalService: TerminalAdministrationService,
		private fb: FormBuilder,
		private translateService: TranslateService,
	) {
		if (dialogData.worktask == null) {
			this.worktask = {
				id: null,
				alias: '',
				name: '',
				isActive: true,
				tags: {},
			};
		} else {
			this.isEdit = true;
			this.worktask = { ...dialogData.worktask };
		}
	}

	ngOnInit(): void {
		this.translateService.get('mes.administration.worktasks.dialog.tags.category-name.uncategorized-name').subscribe((label: string) => {
			this.uncategorizedName = label;
		});
		this.terminalService.getTerminals().subscribe((terminals) => {
			this.terminals = [{ id: '', name: '' } as ITerminal, ...terminals.result.sort((a, b) => a.alias.localeCompare(b.alias))];
		});
		this.initializeForm();
		this.categoryControl = this.controls['categoryName'] as FormControl<string>;
		this.categoryFilteredOptions = this.categoryControl.valueChanges.pipe(
			startWith(''),
			map((value) => {
				const filterValue = value.toLowerCase();
				return this.dialogData.categoryNames.filter((option) => option.toLowerCase().includes(filterValue));
			}),
		);
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.worktask.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.worktask.name, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			categoryName: [this.worktask.tags['categoryName'] ?? '', [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			terminal: [this.worktask.tags['terminal'] ?? ''],
			salarytype: [this.worktask.tags['salarytype'] ?? '', [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			isActive: [this.worktask.isActive],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key === 'terminal' || key === 'salarytype' || key === 'categoryName') {
					this.worktask.tags[key] = (value[key] ?? '') !== '' ? value[key] : undefined;
				} else if (key in this.worktask) {
					this.worktask[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	onCancel = () => this.dialogRef.close();

	async onSave() {
		this.httpError = '';
		if (this.form.valid) {
			if (this.loading) return;
			this.loading = true;
			if (this.worktask.id == null) {
				this.worktaskService.create(this.worktask).subscribe({
					next: () => {
						this.saved.emit();
						this.dialogRef.close();
					},
					error: (errorResponse) => {
						this.httpError = `${errorResponse.status}`;
						this.loading = false;
					},
				});
			} else {
				this.worktaskService.update(this.worktask).subscribe({
					next: () => {
						this.saved.emit();
						this.dialogRef.close();
					},
					error: (errorResponse) => {
						this.httpError = `${errorResponse.status}`;
						this.loading = false;
					},
				});
			}
		}
	}

	displayCategoryOptions(category: string): string {
		return category !== '' ? category : this.uncategorizedName;
	}
}
